/**
 * Types + math for a person's project assignments and their forward guidance.
 *
 * The assignment shape mirrors the `person-demands` gateway endpoint one-for-one
 * (see `docs/nos-gateway-endpoints.md`) so wiring the real feed is a matter of
 * mapping columns onto `PersonProjectAssignment`, not reshaping the UI. Guidance
 * has no feed yet — it comes from the separate guidance-setting app.
 */

/** How many months forward the guidance window covers. */
export const GUIDANCE_WINDOW_MONTHS = 4;

/**
 * A month guided below this share of capacity counts as under-guided. The whole
 * point of the tab (WI-118) is spotting this before the time goes unfilled.
 */
export const UNDER_GUIDED_PERCENT = 80;

/**
 * Guided above this share of capacity is over-committed — someone booked
 * beyond what they can actually deliver. Strictly above: a month planned to
 * exactly 100% is fully booked, which is the goal, not a problem.
 */
export const OVER_COMMITTED_PERCENT = 100;

/**
 * Logged below this share of capacity for a completed month reads as "didn't
 * log their time". Deliberately well under the under-guided gate: this is
 * about timesheets not being filled in, not about someone being quiet, so it
 * should only fire when the gap is too large to be anything else.
 */
export const LOW_LOGGED_PERCENT = 50;

/** One month of a single assignment — what was planned, and what was worked. */
export interface GuidanceMonth {
  /** Calendar month key, `YYYY-MM`. */
  month: string;
  /** Guided hours for the month, from `DemandPlan`. Null means no guidance was set. */
  hours: number | null;
  /** Guided share of the person's capacity, 0–100. Null means not expressed as a share. */
  allocationPercent: number | null;
  /**
   * Hours actually logged that month, from `TimeEntry`. Null means nothing was
   * logged — distinct from `0`, which no feed produces (a month with no time
   * simply has no row).
   */
  loggedHours: number | null;
}

/**
 * Provenance for one assignment's guidance. Null on an assignment `DemandPlan`
 * has no rows for at all, which is the "nobody has planned this" signal.
 *
 * Every field here is currently null: `DemandPlan` carries no note, author or
 * timestamp column. Kept because the guidance-setting app is where those would
 * come from, and the UI already renders them when present.
 */
export interface ProjectGuidance {
  /** Free-text direction from whoever set the guidance. */
  note: string | null;
  setBy: string | null;
  /** Date-only ISO (`YYYY-MM-DD`). */
  setAt: string | null;
}

/** One `DemandPerson` row — a person's assignment to a demand. */
export interface PersonProjectAssignment {
  /** `DemandPerson.DemandPersonID`. */
  id: string;
  demandId: string;
  demandName: string;
  accountName: string | null;
  /** Distinct project names drawn from logged time. Empty when nothing is logged. */
  projectNames: string[];
  /** Hours logged against this assignment to date. */
  hoursLogged: number;
  demandStatus: string | null;
  assignmentStatus: string | null;
  role: string | null;
  roleLevel: string | null;
  isResponsible: boolean;
  isAccountable: boolean;
  /** Date-only ISO (`YYYY-MM-DD`). */
  startDate: string | null;
  endDate: string | null;
  /**
   * Computed by NOS, not stored — an assignment is only active when the demand
   * is open, the assignment status is Onboarding/Committed/Active, and today
   * falls inside its date range. See `docs/nos-gateway-endpoints.md`.
   */
  isActiveNow: boolean;
  /**
   * Every month the assignment's SOW spans, earliest first — not a fixed
   * forward window. Both feeds clamp to the assignment's own
   * StartDate..EndDate (flooring at the current month when there is no start
   * date), so this is the shape of that SOW.
   *
   * It is the UNION of the two feeds: a month appears if `DemandPlan` guided it
   * OR time was logged against it. Neither alone is the SOW's real extent —
   * work gets logged in months nobody planned, and months get planned that
   * nobody has worked yet.
   */
  months: GuidanceMonth[];
  guidance: ProjectGuidance | null;
}

/** The calendar current month as a `YYYY-MM` key. */
/** The month before `month` (`YYYY-MM` in, `YYYY-MM` out). */
export function previousMonthKey(month: string): string {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function currentMonthKey(from: Date = new Date()): string {
  return `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * How many months of history the summary banner shows alongside the forward
 * window. Logged hours only exist for months that have happened, so a
 * forward-only banner would have nothing to report for them.
 */
export const GUIDANCE_HISTORY_MONTHS = 3;

/**
 * Month keys (`YYYY-MM`) spanning `back` months before the month `from` falls
 * in, that month itself, and `forward` months after it. Derived rather than
 * hard-coded so a window never reads stale, and built through `Date` so
 * crossing a year boundary can't produce month 0 or 13.
 */
export function monthWindow(back: number, forward: number, from: Date = new Date()): string[] {
  const out: string[] = [];
  for (let i = -back; i <= forward; i++) {
    const d = new Date(from.getFullYear(), from.getMonth() + i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

/** `"2026-08"` → `"Aug"`, or `"Jan '27"` when the year needs stating. */
function guidanceMonthLabel(month: string, withYear = false): string {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  const short = new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short" });
  return withYear ? `${short} '${String(y).slice(2)}` : short;
}

/** `"2026-11"` → `"November"`. For prose, where the short form reads clipped. */
export function monthLongLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long" });
}

/**
 * Label every month in a window, stating the year only on months that fall
 * outside the first month's year — so a window crossing January stays readable
 * without repeating "'26" four times.
 */
export function labelWindow(window: string[]): { month: string; label: string }[] {
  const baseYear = window[0]?.slice(0, 4);
  return window.map((month) => ({
    month,
    label: guidanceMonthLabel(month, month.slice(0, 4) !== baseYear),
  }));
}

/**
 * Guided hours, capacity share and logged hours per window month, totalled
 * across the given assignments. A month no assignment guides totals to null
 * (not 0) so "nobody has set guidance" reads differently from "deliberately
 * guided to zero".
 */
export function summarizeGuidance(
  assignments: PersonProjectAssignment[],
  window: string[],
): GuidanceMonth[] {
  return window.map((month) => {
    let hours: number | null = null;
    let allocationPercent: number | null = null;
    let loggedHours: number | null = null;
    for (const a of assignments) {
      const entry = a.months.find((m) => m.month === month);
      if (!entry) continue;
      if (entry.hours !== null) hours = (hours ?? 0) + entry.hours;
      if (entry.allocationPercent !== null) {
        allocationPercent = (allocationPercent ?? 0) + entry.allocationPercent;
      }
      if (entry.loggedHours !== null) loggedHours = (loggedHours ?? 0) + entry.loggedHours;
    }
    return { month, hours, allocationPercent, loggedHours };
  });
}

/**
 * The first window month guided below `UNDER_GUIDED_PERCENT`, treating "no
 * guidance set" as under-guided — an unguided month is the strongest version of
 * the signal, not an absence of one.
 */
export function firstUnderGuidedMonth(months: GuidanceMonth[]): GuidanceMonth | null {
  return months.find((m) => (m.allocationPercent ?? 0) < UNDER_GUIDED_PERCENT) ?? null;
}

/**
 * The demand name with a leading repeat of the account name removed —
 * `("CraneTech - T&M Support - SOW#3", "CraneTech")` → `"T&M Support - SOW#3"`.
 *
 * NOS demand names usually embed the account ("PGT Trucking Support SOW #1",
 * "The Kraft Group Account Management"), so a tile that shows the account as
 * its headline and the demand beneath it prints the account twice. Only a
 * *leading* match is stripped, and only when something is left over — an
 * account whose demand is named after it exactly (or a mid-string mention)
 * keeps the full name rather than collapsing to nothing.
 */
export function stripAccountPrefix(demandName: string, accountName: string | null): string {
  if (!accountName) return demandName;
  const account = accountName.trim();
  if (account.length === 0) return demandName;
  if (!demandName.toLowerCase().startsWith(account.toLowerCase())) return demandName;

  // Drop the separator NOS put between the two — " - ", " – ", ": " or a space.
  const rest = demandName.slice(account.length).replace(/^[\s:–—-]+/, "");
  return rest.length > 0 ? rest : demandName;
}

/** `"Engineer — Senior"`, either half optional. */
export function formatRoleLabel(role: string | null, roleLevel: string | null): string | null {
  if (role && roleLevel) return `${role} — ${roleLevel}`;
  return role ?? roleLevel;
}

/**
 * Guided hours totalled across every month a SOW spans — the "total guidance"
 * figure on a project's detail popup.
 *
 * Unguided months are skipped rather than counted as zero, and a SOW with no
 * guided month at all totals to null: "nobody has planned this" has to stay
 * distinguishable from "deliberately guided to zero", which is the same
 * distinction `GuidanceMonth.hours` carries per month.
 */
export function sumGuidedHours(months: GuidanceMonth[]): number | null {
  let total: number | null = null;
  for (const m of months) {
    if (m.hours !== null) total = (total ?? 0) + m.hours;
  }
  return total;
}

/** One month's cell in the year-by-year grid. */
export interface GuidanceGridCell {
  /** Calendar month key, `YYYY-MM`. */
  month: string;
  /** 0-11. The grid is a calendar, so a cell's column is its calendar month. */
  monthIndex: number;
  /**
   * Whether the month falls within the SOW at all. False cells render blank
   * rather than as "—": a SOW starting in September did not have eight months
   * of nothing guided, it simply did not exist yet.
   */
  inSpan: boolean;
  /**
   * The month's figures, or null when neither feed reported it. Inside the span
   * that means "no data" (a real "—"); outside it means nothing at all.
   */
  entry: GuidanceMonth | null;
}

/**
 * One calendar year of a SOW — always twelve cells, whatever the SOW covers.
 * Not exported: callers reach it through `buildGuidanceGrid`'s return type.
 */
interface GuidanceGridYear {
  year: number;
  cells: GuidanceGridCell[];
}

/**
 * A SOW's months arranged as a calendar: twelve columns, one row per year.
 *
 * This replaces a single scrolling strip of months. A five-year SOW is 60
 * cells, which no popup can show in one line, but it is only five rows — and
 * as a calendar it also aligns each month above the same month a year later,
 * which the linear strip could not do.
 *
 * The span runs from the earliest reported month to the latest. Months between
 * those that neither feed reported are still in-span (the SOW was running,
 * nothing was guided or logged); months outside them are not.
 *
 * Rows run newest year first: on a five-year SOW the months anyone is actually
 * asking about are the recent ones, and they should not be at the bottom.
 */
export function buildGuidanceGrid(months: GuidanceMonth[]): GuidanceGridYear[] {
  if (months.length === 0) return [];

  // Sorted defensively rather than trusting the caller: `YYYY-MM` keys compare
  // correctly as strings, so this is a plain lexical min/max.
  const keys = months.map((m) => m.month).sort();
  const first = keys[0];
  const last = keys[keys.length - 1];
  if (first === undefined || last === undefined) return [];

  const firstYear = Number(first.slice(0, 4));
  const lastYear = Number(last.slice(0, 4));
  if (!Number.isFinite(firstYear) || !Number.isFinite(lastYear)) return [];

  const byMonth = new Map(months.map((m) => [m.month, m]));
  const years: GuidanceGridYear[] = [];
  for (let year = lastYear; year >= firstYear; year--) {
    const cells: GuidanceGridCell[] = [];
    for (let i = 0; i < 12; i++) {
      const month = `${year}-${String(i + 1).padStart(2, "0")}`;
      cells.push({
        month,
        monthIndex: i,
        inSpan: month >= first && month <= last,
        entry: byMonth.get(month) ?? null,
      });
    }
    years.push({ year, cells });
  }
  return years;
}

/**
 * Whether an assignment is Nymbl's own work rather than a client's, decided by
 * the **account** name carrying "nymbl".
 *
 * Deliberately not `Account.IsBillable`: a client account can carry an
 * individual non-billable demand, and that is still client work that belongs
 * with the rest of the account's.
 *
 * Deliberately the account name and not the demand name either — dev NOS has a
 * Jitterbit demand titled "Madison Energy - Jitterbit JB_Nymbl_2023_Reseller_
 * Agreement", and reading the demand name would file that client engagement as
 * internal.
 *
 * A demand with no account counts as client work: internal work in NOS always
 * hangs off the Nymbl Internal account, so a missing account is a data gap.
 */
export function isInternalAccount(accountName: string | null): boolean {
  return accountName?.toLowerCase().includes("nymbl") ?? false;
}
